import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { SignUpSchema } from '@/lib/zod-schemas/auth.schema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authClient } from '@/lib/better-auth/client';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { LoaderIcon } from 'lucide-react';
import { useCheckUsername } from '@/lib/tanstack-query/hooks/useCheckUsername';
import { useState } from 'react';

export function SignUpForm() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const form = useForm<z.infer<typeof SignUpSchema>>({
    resolver: zodResolver(SignUpSchema),
    defaultValues: {
      username: '',
      email: '',
      password: '',
    },
  });

  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { mutateAsync: checkUsername, isPending: isPendingCheckUsername } =
    useCheckUsername();

  const signUp = async (data: SignUpSchema) => {
    const usernameCheck = await checkUsername({
      username: data.username,
    });

    if (!usernameCheck.success) {
      throw new Error('USERNAME_TAKEN');
    }

    const { error, data: response } = await authClient.signUp.email({
      email: data.email,
      password: data.password,
      name: data.username,
      username: data.username,
    });

    if (error) {
      throw new Error(error.message);
    }

    return response;
  };

  const signUpMutation = useMutation({
    mutationFn: signUp,
    onSuccess: () => {
      queryClient.resetQueries();
    },
  });
  const { isPending: isLoading } = signUpMutation;

  const handleSubmit = async (values: z.infer<typeof SignUpSchema>) => {
    try {
      await signUpMutation.mutateAsync(values);
      form.reset();
      setSuccessMessage('We have sent you a verification email.');
    } catch (error: any) {
      if (error.message === 'USERNAME_TAKEN') {
        form.setError('username', {
          type: 'manual',
          message: 'Username is already taken',
        });
      } else if (error.message === 'User already exists. Use another email.') {
        form.setError('email', {
          type: 'manual',
          message: 'User already exists. Use another email.',
        });
      } else {
        form.setError('root', {
          type: 'manual',
          message: 'Something went wrong. Please try again.',
        });
      }
    }
  };

  const signUpGoogle = () => {
    authClient.signIn.social({ provider: 'google' });
  };

  return (
    <div className="flex w-full flex-col justify-center">
      <div>
        <h2 className="text-dark-1 mt-6 text-center text-3xl font-extrabold dark:text-white">
          Sign up to Would <span className="text-primary-wu"> U</span>
        </h2>
      </div>

      <div className="mt-8 flex flex-col gap-6 sm:mx-auto sm:w-full sm:max-w-md">
        <Button
          onClick={() => signUpGoogle()}
          variant="outline"
          className="w-full rounded-full"
        >
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
          >
            <path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            ></path>
            <path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            ></path>
            <path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            ></path>
            <path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            ></path>
            <path fill="none" d="M0 0h48v48H0z"></path>
          </svg>
          Sign up with Google
        </Button>
        <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
          <span className="text-muted-foreground dark:bg-background relative z-10 bg-white px-2">
            Or continue with
          </span>
        </div>
        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={form.handleSubmit(handleSubmit)}
            noValidate
          >
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="username">Username</Label>
                  <FormControl>
                    <div className="mt-1">
                      <Input
                        {...field}
                        id="username"
                        name="username"
                        type="text"
                        maxLength={50}
                        className="h-12 text-base"
                        placeholder="Enter your Username"
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="email">Email</Label>
                  <FormControl>
                    <div className="mt-1">
                      <Input
                        {...field}
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        maxLength={50}
                        className="h-12 text-base"
                        placeholder="Enter your email"
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="password">Password</Label>
                  <FormControl>
                    <div className="mt-1">
                      <Input
                        {...field}
                        id="password"
                        name="password"
                        type="password"
                        autoComplete={'new-password'}
                        className="h-12 text-base"
                        placeholder="Enter your password"
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />
            <div>
              <Button
                type="submit"
                variant={'default'}
                className="w-full rounded-full"
                disabled={false}
              >
                {isLoading ? (
                  <>
                    <LoaderIcon className="animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Sign up'
                )}
              </Button>
            </div>
            {form.formState.errors.root && (
              <p className="text-sm text-red-500">
                {form.formState.errors.root.message}
              </p>
            )}

            {successMessage && (
              <p className="text-center text-sm font-medium text-green-600">
                {successMessage}
              </p>
            )}
          </form>
        </Form>
        <div>
          <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
            <span className="text-muted-foreground dark:bg-background relative z-10 bg-white px-2">
              Already have an account?
            </span>
          </div>

          <div className="mt-6">
            <Button
              variant={'secondary'}
              onClick={() => navigate({ to: '/sign-in' })}
              className="w-full rounded-full"
            >
              Sign in to existing account
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
